import { Button, Table, Modal } from "@telluscompany/design-system";
import { useSearchParams } from "react-router-dom";
import { PlusCircle } from "lucide-react";
import { DeleteModal } from "@telluscompany/design-system";
import { useLocalization, translateOperationalModule } from "@i18n";
import { useCrudModals } from "@shared/hooks";
import { Gs3TablePaginator } from "@shared/components";
import { EMPTY_PAGINATION_META } from "@shared/constants/pagination.constants";
import type {
  LicenseListItem,
  LicenseFilters,
} from "../../api/licenses/licenses.types";
import type { LicenseFormData, UpdateLicenseFormData } from "../../schemas";
import {
  useCreateLicenseMutation,
  useUpdateLicenseMutation,
  useDeleteLicenseMutation,
} from "../../api/licenses/licenses.mutations";
import { CreateLicenseForm } from "./CreateLicenseForm";
import { EditLicenseForm } from "./EditLicenseForm";
import { LicensesFilter } from "./LicensesFilter";
import { LicensesActionMenu } from "./LicensesActionMenu";
import { PaginationMeta } from "@shared/api";
import { useUpdateTableUserPreferences } from "@shared/hooks";
import { useUserContext } from "@/shared/contexts";

interface LicensesTabProps {
  tenantId: string;
  licenses: LicenseListItem[];
  isLoading?: boolean;
  onFilter: (filters: Partial<LicenseFilters>) => void;
  onClearFilter: () => void;
  initialFilters?: Partial<LicenseFilters>;
  paginatedMeta?: PaginationMeta;
}

export const LicensesTab = ({
  tenantId,
  licenses,
  onFilter,
  onClearFilter,
  initialFilters,
  paginatedMeta,
}: LicensesTabProps) => {
  const { translate } = useLocalization();

  const {
    modals,
    selectedItem: selectedLicense,
    openCreate,
    openEdit,
    openDelete,
    closeCreate,
    closeEdit,
    closeDelete,
  } = useCrudModals<LicenseListItem>();

  const selectedLicenseId = selectedLicense?.id ?? "";
  const createMutation = useCreateLicenseMutation(tenantId);
  const updateMutation = useUpdateLicenseMutation(tenantId, selectedLicenseId);
  const deleteMutation = useDeleteLicenseMutation(tenantId);

  const { user } = useUserContext();
  const updateTableUserPreferences = useUpdateTableUserPreferences(user?.userPreferences);

  const handleCreateLicense = async (data: LicenseFormData) => {
    await createMutation.mutateAsync({
      structureId: data.structureId,
      moduleId: data.moduleId,
      quantity: data.quantity,
      startDate: data.startDate.toISOString(),
      endDate: data.endDate.toISOString(),
      managementEmail: data.managementEmail,
      clientEmail: data.clientEmail,
      notificationDays: data.notificationDays,
    });
    closeCreate();
  };

  const handleEditLicense = async (data: UpdateLicenseFormData) => {
    await updateMutation.mutateAsync({
      managementEmail: data.managementEmail,
      clientEmail: data.clientEmail,
    });
    closeEdit();
  };

  const handleDeleteLicense = () => {
    if (selectedLicense?.id) {
      deleteMutation.mutate(selectedLicense.id, {
        onSuccess: () => closeDelete(),
      });
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const createActionMenu = (rowData: { rowData: LicenseListItem }) => {
    const license = rowData.rowData;

    return (
      <LicensesActionMenu
        license={license}
        onEdit={() => openEdit(license)}
        onDelete={() => openDelete(license)}
      />
    );
  };

  const tableData = (licenses ?? []).map((license) => ({
    ...license,
    module: translateOperationalModule(license.module, translate),
    startDate: `${formatDate(license.startDate)} - ${formatDate(license.endDate)}`,
  })) as unknown as Record<string, unknown>[];

  const tableColumns = [
    {
      key: "structure",
      label: translate("management.licenses.table.column.structure"),
      sortType: "text" as const,
      minWidth: "150px",
    },
    {
      key: "complex",
      label: translate("management.licenses.table.column.complex"),
      sortType: "text" as const,
      minWidth: "150px",
    },
    {
      key: "module",
      label: translate("management.licenses.table.column.module"),
      sortType: "text" as const,
      minWidth: "150px",
    },
    {
      key: "quantity",
      label: translate("management.licenses.table.column.licenses"),
      minWidth: "100px",
    },
    {
      key: "startDate",
      label: translate("management.licenses.table.column.accessPeriod"),
      minWidth: "200px",
      sortType: "text" as const,
    },
  ];

  return (
    <div className="p-4">
      <Table<LicenseListItem>
        userId="LICENSE1"
        usersPreferencesFromServer={user?.userPreferences}
        updateUsersPreferencesOnServer={updateTableUserPreferences}
        useSearchParamsHook={useSearchParams}
        enableSortByUrl
        TableActionMenu={ user?.role === "owner" ? createActionMenu as React.ComponentType : undefined}
        TableButtons={
          user?.role === "owner" && (
          <Button
            size="small"
            variant="primary"
            onClick={openCreate}
            iconLeft={<PlusCircle />}
          >
            {translate("management.licenses.button.new")}
          </Button>
          )
        }
        TableFilter={() => (
          <LicensesFilter
            onFilter={onFilter}
            onClear={onClearFilter}
            initialFilters={initialFilters}
          />
        )}
        TablePaginator={
          <Gs3TablePaginator
            meta={paginatedMeta ?? EMPTY_PAGINATION_META}
            useUrlState
            useSearchParamsHook={useSearchParams}
          />
        }
        draggable
        enableTableColumnsSelector
        tableColumns={tableColumns}
        tableData={tableData}
        tableId="licenses-table"
        filterButtonText={translate(
          "management.licenses.table.filterButtonText",
        )}
      />

      {/* Modal de Criação */}
      <Modal
        open={modals.create}
        onClose={closeCreate}
        title={translate("management.licenses.modal.title.create")}
        content={
          <CreateLicenseForm
            tenantId={tenantId}
            onSubmit={handleCreateLicense}
            onCancel={closeCreate}
            isSubmitting={createMutation.isPending}
          />
        }
      />

      {/* Modal de Edição */}
      <Modal
        open={modals.edit}
        onClose={closeEdit}
        title={translate("management.licenses.modal.title.edit")}
        content={
          <EditLicenseForm
            tenantId={tenantId}
            licenseId={selectedLicenseId}
            onSubmit={handleEditLicense}
            onCancel={closeEdit}
            isSubmitting={updateMutation.isPending}
          />
        }
      />

      {/* Modal de Exclusão */}
      <DeleteModal
        open={modals.delete}
        onClose={closeDelete}
        onConfirm={handleDeleteLicense}
        isDeleting={deleteMutation.isPending}
        itemDescription={selectedLicense?.structure}
        title={translate("common.deleteModalTitle")}
        message={translate("common.deleteModalItemMessage")}
        alertMessage={translate("common.deleteModalAlertMessage")}
        confirmButtonText={translate("common.delete")}
        cancelButtonText={translate("common.cancel")}
        deletingButtonText={translate("common.deleting")}
      />
    </div>
  );
};

export default LicensesTab;
